// ─── Action Executor ──────────────────────────────────────────────────────────
//
// Phase 3 execution layer. Implements all 21 action types using Phase 3 APIs:
//   - CRUD → collectionEngine (Q6: app:record_* events guaranteed)
//   - State ops → appState (app:state_changed / app:state_reset emitted)
//   - Conditions → compileExpression from @/state/stateHydration (Q5)
//   - navigate → commandDispatcher (app:navigate Command Event)
//   - show_notification → commandDispatcher (app:notification Command Event)
//   - emit_event → eventBus.emit with user-declared event name
//
// Replay safety: Command Events are suppressed when replayModeActive = true.
// Duration measurement: performance.now() for durationMs only (not for log timestamps).
// Log timestamps: executionTracer uses sequence counters (no Date.now()).
//
// Protected files this module does NOT import from:
//   @/appEngine/dataEngine  (uses collectionEngine only)
//   @/appEngine/stateEngine (uses appState re-exported from appStateStore)

import { appState } from '@/state/appStateStore';
import { compileExpression } from '@/state/stateHydration';
import { sideEffectManager } from './sideEffectManager';
import { executionTracer } from './executionTracer';
import { actionRegistry } from './actionRegistry';
import { commandDispatcher } from '@/events/commandDispatcher';
import { eventBus } from '@/events/eventBus';
import {
  insertRecord,
  updateRecord,
  deleteRecord,
  queryCollection,
} from '@/data/collectionEngine';
import { permissionEngine } from '@/appEngine/permissionEngine';
import type {
  ActionDefinition,
  SetStateAction,
  ToggleStateAction,
  InsertRecordAction,
  UpdateRecordAction,
  DeleteRecordAction,
  NavigateAction,
  ShowNotificationAction,
  ChainAction,
  ConditionalAction,
  SwitchCaseAction,
  LoopAction,
  GuardAction,
  TryCatchAction,
  RetryAction,
  EmitEventAction,
  ParallelAction,
  FilterCollectionAction,
  ResetStateAction,
  DelayAction,
  BatchAction,
} from '@/core/schema/actionSchema';
import type { StateValue } from '@/appEngine/stateEngine';

// ─── Replay Mode ──────────────────────────────────────────────────────────────

let replayModeActive = false;

/** Called by replayEngine before/after replay. Suppresses Command Event emission. */
export function setReplayMode(active: boolean): void {
  replayModeActive = active;
}

export function isInReplayMode(): boolean {
  return replayModeActive;
}

// ─── Delay Clock Injection ────────────────────────────────────────────────────

let delayFn: (ms: number) => Promise<void> = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Override the delay clock. Pass `() => Promise.resolve()` for replay/tests. */
export function setDelayFn(fn: (ms: number) => Promise<void>): void {
  delayFn = fn;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ActionExecutionResult {
  actionId: string;
  actionType: string;
  success: boolean;
  durationMs: number;
  error?: string;
  output?: unknown;
}

// ─── Executor ─────────────────────────────────────────────────────────────────

class ActionExecutorImpl {
  /**
   * Execute an action by ID. Performs permission check, runs the action,
   * records the trace entry, and emits app:action_executed or app:action_failed.
   */
  async execute(actionId: string, projectId: string): Promise<ActionExecutionResult> {
    const def = actionRegistry.get(actionId);
    if (!def) {
      const result: ActionExecutionResult = {
        actionId,
        actionType: 'unknown',
        success: false,
        durationMs: 0,
        error: `Action "${actionId}" not found in registry`,
      };
      eventBus.emit({ type: 'app:action_failed', projectId, actionId, actionType: 'unknown', reason: result.error! });
      return result;
    }

    // Permission check
    if (def.allowedRoles && def.allowedRoles.length > 0) {
      const access = permissionEngine.checkActionAccess(actionId);
      if (!access.allowed) {
        const reason = access.reason ?? 'Permission denied';
        executionTracer.record({
          actionId,
          actionType: def.type,
          success: false,
          durationMs: 0,
          error: reason,
          sideEffects: [],
        });
        eventBus.emit({ type: 'app:action_failed', projectId, actionId, actionType: def.type, reason });
        return { actionId, actionType: def.type, success: false, durationMs: 0, error: reason };
      }
    }

    const t0 = performance.now();

    try {
      const output = await this.executeType(def, projectId);
      const durationMs = Math.round(performance.now() - t0);
      const effects = sideEffectManager.flush();

      executionTracer.record({
        actionId,
        actionType: def.type,
        success: true,
        durationMs,
        sideEffects: effects,
      });

      eventBus.emit({ type: 'app:action_executed', projectId, actionId, actionType: def.type, durationMs });

      return { actionId, actionType: def.type, success: true, durationMs, output };
    } catch (e) {
      const durationMs = Math.round(performance.now() - t0);
      const error = e instanceof Error ? e.message : String(e);
      const effects = sideEffectManager.flush();

      executionTracer.record({
        actionId,
        actionType: def.type,
        success: false,
        durationMs,
        error,
        sideEffects: effects,
      });

      eventBus.emit({ type: 'app:action_failed', projectId, actionId, actionType: def.type, reason: error });

      return { actionId, actionType: def.type, success: false, durationMs, error };
    }
  }

  // ─── Action Type Implementations ────────────────────────────────────────────

  private async executeType(def: ActionDefinition, projectId: string): Promise<unknown> {
    switch (def.type) {

      case 'set_state': {
        const { key, value } = def as SetStateAction;
        const parts = key.split(':');
        const scope = parts[0] as 'global' | 'page' | 'component';
        const success = appState.set(key, value as StateValue, 'action');
        if (success) {
          sideEffectManager.record({ kind: 'state_changed', stateKey: key });
          eventBus.emit({ type: 'app:state_changed', projectId, stateId: def.id, key, scope });
        }
        return success;
      }

      case 'toggle_state': {
        const { key } = def as ToggleStateAction;
        const parts = key.split(':');
        const scope = parts[0] as 'global' | 'page' | 'component';
        const current = appState.get(key);
        const success = appState.set(key, !current, 'action');
        if (success) {
          sideEffectManager.record({ kind: 'state_changed', stateKey: key });
          eventBus.emit({ type: 'app:state_changed', projectId, stateId: def.id, key, scope });
        }
        return success;
      }

      case 'insert_record': {
        const { collection, data } = def as InsertRecordAction;
        const result = insertRecord(collection, data);
        if (result.success && result.record) {
          sideEffectManager.record({ kind: 'record_inserted', collection, recordId: result.record._id });
        }
        if (!result.success) {
          throw new Error(result.errors?.map(e => e.message).join('; ') ?? 'Insert failed');
        }
        return result.record;
      }

      case 'update_record': {
        const { collection, recordId, data } = def as UpdateRecordAction;
        const result = updateRecord(collection, recordId, data);
        if (result.success) {
          sideEffectManager.record({ kind: 'record_updated', collection, recordId });
        }
        if (!result.success) {
          throw new Error(result.errors?.map(e => e.message).join('; ') ?? 'Update failed');
        }
        return result;
      }

      case 'delete_record': {
        const { collection, recordId } = def as DeleteRecordAction;
        const success = deleteRecord(collection, recordId);
        if (success) {
          sideEffectManager.record({ kind: 'record_deleted', collection, recordId });
        }
        if (!success) {
          throw new Error(`Delete failed: record "${recordId}" not found in "${collection}"`);
        }
        return success;
      }

      case 'navigate': {
        const { route, replace } = def as NavigateAction;
        if (!replayModeActive) {
          commandDispatcher.emit({ type: 'app:navigate', projectId, route, replace: replace ?? false });
        }
        return { route, replace };
      }

      case 'show_notification': {
        const { message, variant, duration } = def as ShowNotificationAction;
        if (!replayModeActive) {
          commandDispatcher.emit({ type: 'app:notification', projectId, message, variant, duration });
        }
        return { message, variant };
      }

      case 'emit_event': {
        const { event, payload } = def as EmitEventAction;
        // Emit the user-declared event name - NOT state:changed (which was the bug in actionEngine.ts)
        eventBus.emit({ type: event as any, ...(payload ?? {}), projectId });
        return { event };
      }

      case 'validate_form': {
        // Deferred - pending System 5 Component/Form Registry.
        // Future integration target: validationEngine.validateRecord(collection, currentData)
        // where collection + currentData come from the form registry resolving formId.
        return { deferred: true, reason: 'validate_form requires System 5 form registry' };
      }

      case 'api_call': {
        // Deferred (Q4) - no outbound HTTP until sandboxed capability layer.
        return { deferred: true, reason: 'api_call requires sandboxed capability layer (Q4)' };
      }

      case 'conditional': {
        const { condition, thenActionId, elseActionId } = def as ConditionalAction;
        const condFn = compileExpression(condition);
        const stateDeps = buildStateDeps();
        const result = condFn(stateDeps);
        const targetId = result ? thenActionId : elseActionId;
        if (targetId) {
          return await this.execute(targetId, projectId);
        }
        return null;
      }

      case 'switch_case': {
        const { expression, cases, defaultActionId } = def as SwitchCaseAction;
        const exprFn = compileExpression(expression);
        const stateDeps = buildStateDeps();
        const value = exprFn(stateDeps);
        const matched = cases.find(c => c.value === value);
        const targetId = matched ? matched.actionId : defaultActionId;
        if (targetId) {
          return await this.execute(targetId, projectId);
        }
        return null;
      }

      case 'guard': {
        const { condition, actionId } = def as GuardAction;
        const guardFn = compileExpression(condition);
        const stateDeps = buildStateDeps();
        const passes = !!guardFn(stateDeps);
        if (!passes) return null;
        return await this.execute(actionId, projectId);
      }

      case 'chain': {
        const { actions } = def as ChainAction;
        const results: ActionExecutionResult[] = [];
        for (const id of actions) {
          const r = await this.execute(id, projectId);
          results.push(r);
          if (!r.success) {
            throw new Error(`Chain failed at action "${id}": ${r.error}`);
          }
        }
        return results;
      }

      case 'parallel': {
        const { actions } = def as ParallelAction;
        const results = await Promise.all(actions.map(id => this.execute(id, projectId)));
        const failed = results.find(r => !r.success);
        if (failed) {
          throw new Error(`Parallel action "${failed.actionId}" failed: ${failed.error}`);
        }
        return results;
      }

      case 'batch': {
        const { actions } = def as BatchAction;
        const results: ActionExecutionResult[] = [];
        for (const id of actions) {
          const r = await this.execute(id, projectId);
          results.push(r);
          if (!r.success) {
            throw new Error(`Batch failed at action "${id}": ${r.error}`);
          }
        }
        return results;
      }

      case 'loop': {
        const { collection, actionId, variable } = def as LoopAction;
        const records = queryCollection(collection);
        const results: ActionExecutionResult[] = [];
        for (const record of records) {
          // Set the loop variable in state if it's a valid state key
          if (variable) {
            appState.set(variable, record as unknown as StateValue, 'action');
          }
          const r = await this.execute(actionId, projectId);
          results.push(r);
          if (!r.success) break;
        }
        return results;
      }

      case 'try_catch': {
        const { tryActionId, catchActionId } = def as TryCatchAction;
        const tryResult = await this.execute(tryActionId, projectId);
        if (!tryResult.success) {
          return await this.execute(catchActionId, projectId);
        }
        return tryResult;
      }

      case 'retry': {
        const { actionId, maxAttempts, delayMs } = def as RetryAction;
        const attempts = Math.min(maxAttempts, 10);
        let lastResult: ActionExecutionResult = {
          actionId,
          actionType: 'unknown',
          success: false,
          durationMs: 0,
          error: 'No attempts made',
        };
        for (let attempt = 0; attempt < attempts; attempt++) {
          lastResult = await this.execute(actionId, projectId);
          if (lastResult.success) return lastResult;
          if (attempt < attempts - 1 && delayMs && delayMs > 0) {
            await delayFn(delayMs * (attempt + 1));
          }
        }
        throw new Error(`Retry exhausted after ${attempts} attempt(s): ${lastResult.error}`);
      }

      case 'delay': {
        const { ms } = def as DelayAction;
        await delayFn(ms);
        return { ms };
      }

      case 'filter_collection': {
        const { collection, condition, stateKey } = def as FilterCollectionAction;
        const records = queryCollection(collection);
        const filterFn = compileExpression(condition);
        const filtered = records.filter(record => {
          const deps = { ...record } as Record<string, StateValue>;
          return !!filterFn(deps);
        });
        const parts = stateKey.split(':');
        const scope = parts[0] as 'global' | 'page' | 'component';
        appState.set(stateKey, filtered as unknown as StateValue, 'action');
        sideEffectManager.record({ kind: 'state_changed', stateKey });
        eventBus.emit({ type: 'app:state_changed', projectId, stateId: def.id, key: stateKey, scope });
        return filtered;
      }

      case 'reset_state': {
        const { scope, pageId, componentId } = def as ResetStateAction;
        const ownerId = pageId ?? componentId;
        appState.resetScope(scope, ownerId);
        sideEffectManager.record({ kind: 'state_reset', scope });
        eventBus.emit({ type: 'app:state_reset', projectId, scope, pageId, componentId });
        return { scope };
      }

      default: {
        const exhaustive: never = def;
        throw new Error(`Unhandled action type: ${(exhaustive as ActionDefinition).type}`);
      }
    }
  }
}

export const actionExecutor = new ActionExecutorImpl();

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Lazy proxy over appState for use as expression evaluator deps.
 * Allows condition expressions to reference state keys without exporting all state.
 */
function buildStateDeps(): Record<string, StateValue> {
  return new Proxy({} as Record<string, StateValue>, {
    get(_target, prop: string) {
      return appState.get(prop);
    },
    has(_target, prop: string) {
      return appState.get(prop) !== null;
    },
  });
}
